import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, NGN } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Warning, Calendar, Clock, CalendarCheck } from "@phosphor-icons/react";
import PageSEO from "@/components/PageSEO";

// Compute expected maturity date from start_date (or today if none) + duration string.
// Supports duration formats like "12 Months", "24 Months", etc.
function computeMaturityDate(startIso, duration) {
  if (!duration) return null;
  const m = String(duration).match(/(\d+)\s*month/i);
  if (!m) return null;
  const months = parseInt(m[1], 10);
  const base = startIso ? new Date(startIso) : new Date();
  if (isNaN(base.getTime())) return null;
  const maturity = new Date(base);
  maturity.setMonth(maturity.getMonth() + months);
  return maturity;
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { http.get(`/opportunities/${id}`).then(r => setOpp(r.data)).catch(() => {}); }, [id]);

  if (!opp) return <div className="container-wide py-24 text-slate-500">Loading…</div>;

  const invest = () => {
    if (!user) {
      toast.error("Please sign in to invest.");
      return;
    }
    nav(`/invest/${opp.id}`);
  };

  const maturity = computeMaturityDate(opp.start_date, opp.duration);
  const fmtDate = (d) => d ? d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "TBA";

  const seoTitle = `${opp.name} – ${opp.category.replace('_', ' ')} investment`;
  const seoDesc = (opp.description || "").slice(0, 200);
  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": opp.name,
    "description": opp.description,
    "image": opp.image_url,
    "category": opp.category,
    "brand": { "@type": "Organization", "name": "Mayoto" },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "NGN",
      "price": opp.minimum_investment,
      "availability": opp.status === "open" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": `https://mayotofocus.com/investments/${opp.id}`,
    }
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mayotofocus.com/" },
      { "@type": "ListItem", "position": 2, "name": "Investments", "item": "https://mayotofocus.com/investments" },
      { "@type": "ListItem", "position": 3, "name": opp.name, "item": `https://mayotofocus.com/investments/${opp.id}` }
    ]
  };

  return (
    <div>
      <PageSEO
        title={seoTitle}
        description={seoDesc}
        canonicalPath={`/investments/${opp.id}`}
        image={opp.image_url}
        ogType="product"
        jsonLd={[productLd, breadcrumbLd]}
      />
      <section className="mayo-grain">
        <div className="container-wide py-14">
          <Link to="/investments" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#00b4d8] mb-6"><ArrowLeft size={16} /> Back to opportunities</Link>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div className="rounded-2xl overflow-hidden bg-slate-100 aspect-[4/3]">
              {opp.image_url && <img src={opp.image_url} alt={opp.name} className="h-full w-full object-cover" />}
            </div>
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge className="bg-white text-[#0a1128] border border-slate-200">{opp.category.replace('_',' ').toUpperCase()}</Badge>
                <Badge className={opp.status === "open" ? "bg-[#00b4d8] text-white" : "bg-slate-800 text-white"}>{opp.status.toUpperCase()}</Badge>
              </div>
              <h1 className="font-display font-black text-3xl lg:text-5xl text-[#0a1128] leading-[1.05]" data-testid="opp-detail-title">{opp.name}</h1>
              <p className="mt-4 text-slate-600 leading-relaxed">{opp.description}</p>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <p className="text-xs text-slate-500">Minimum Investment</p>
                  <p className="font-display font-bold text-2xl text-[#0a1128]" data-testid="opp-detail-min">{NGN(opp.minimum_investment)}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <p className="text-xs text-slate-500">Duration</p>
                  <p className="font-display font-bold text-2xl text-[#0a1128] flex items-center gap-2"><Clock size={20} />{opp.duration}</p>
                </div>
                {opp.expected_roi && (
                  <div className="bg-gradient-to-br from-[#d4af37]/10 to-[#00b4d8]/10 border border-[#d4af37]/40 rounded-2xl p-4 col-span-2">
                    <p className="text-xs font-bold tracking-wider text-[#0a1128]/70 uppercase">Expected ROI</p>
                    <p className="font-display font-black text-3xl text-[#0a1128] mt-1" data-testid="opp-detail-roi">{opp.expected_roi}</p>
                    <p className="mt-1 text-[11px] text-slate-500 italic">Projection — not a guarantee. All investments carry risk.</p>
                  </div>
                )}
                {maturity && (
                  <div className="bg-white border border-slate-200 rounded-2xl p-4">
                    <p className="text-xs text-slate-500">Expected Maturity Date</p>
                    <p className="font-display font-bold text-lg text-[#0a1128] flex items-center gap-2 mt-1" data-testid="opp-detail-maturity">
                      <CalendarCheck size={20} className="text-[#00b4d8]" weight="bold" />{fmtDate(maturity)}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500 italic">Based on {opp.duration.toLowerCase()} from opportunity start.</p>
                  </div>
                )}
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <p className="text-xs text-slate-500">Return Structure</p>
                  <p className="font-semibold text-[#0a1128]">{opp.return_structure}</p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-4">
                  <p className="text-xs text-slate-500">Closing Date</p>
                  <p className="font-semibold text-[#0a1128] flex items-center gap-2"><Calendar size={18} />{opp.closing_date ? new Date(opp.closing_date).toLocaleDateString() : "TBA"}</p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button onClick={invest} data-testid="opp-detail-invest" className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] rounded-full px-7 py-6 text-base">Invest Now</Button>
                <Link to="/legal/investment-terms"><Button variant="outline" className="rounded-full px-6 py-6 border-[#0a1128] text-[#0a1128]">View Investment Terms</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container-wide grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="font-display font-bold text-2xl text-[#0a1128]">Investment Terms</h3>
              <p className="mt-3 text-slate-600 whitespace-pre-line leading-relaxed">{opp.investment_terms || "Detailed terms are shared with prospective investors upon request. Governed by Mayoto's published Investment Terms."}</p>
            </div>
          </div>
          <div className="rounded-2xl bg-[#0a1128] text-white p-6">
            <div className="flex items-center gap-2 text-[#d4af37]"><Warning size={20} weight="bold" /><p className="font-display font-bold">Risk Disclosure</p></div>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{opp.risk_information}</p>
            <Link to="/legal/risk" className="mt-4 inline-block text-sm text-[#00b4d8] hover:underline">Read the full Risk Disclosure →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
